import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BookHeart, Users, LogOut, PlusCircle, Images, ScrollText, Gem, ShieldCheck, TreePine, Gift, Mail, ImageIcon, Music4, ChevronDown, UserRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import CoverImageCard from "./CoverImageCard";
import BackgroundMusicCard from "./BackgroundMusicCard";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [coverOpen, setCoverOpen] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);

  const linkCls = (p) =>
    `inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
      pathname.startsWith(p) ? "bg-[#F0E9DC] text-[#2C2A29]" : "text-muted-foreground hover:text-[#2C2A29]"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-[#F9F6F0]/85 backdrop-blur-md border-b border-border">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-full hover:bg-[#F0E9DC]/60 pl-1 pr-2 py-1 transition-colors" data-testid="account-menu-trigger">
              <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <UserRound className="w-5 h-5 text-primary" />
              </span>
              <span className="font-serif text-xl hidden sm:inline">Eterna</span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 bg-white" data-testid="account-menu">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="font-serif text-base">{user?.name}</span>
              <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setCoverOpen(true)} data-testid="account-cover">
              <ImageIcon className="w-4 h-4 mr-2 text-[#81B29A]" /> Foto di copertina
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setMusicOpen(true)} data-testid="account-music">
              <Music4 className="w-4 h-4 mr-2 text-[#8A6D9C]" /> Musica della capsula
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="text-destructive focus:text-destructive" data-testid="account-logout">
              <LogOut className="w-4 h-4 mr-2" /> Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <nav className="flex items-center gap-1">
          <Link to="/ricordi" className={linkCls("/ricordi")} data-testid="nav-memories">
            <BookHeart className="w-4 h-4" /> <span className="hidden sm:inline">Ricordi</span>
          </Link>
          <Link to="/persone" className={linkCls("/persone")} data-testid="nav-people">
            <Users className="w-4 h-4" /> <span className="hidden sm:inline">Legami</span>
          </Link>
          <Link to="/albero-famiglia" className={linkCls("/albero-famiglia")} data-testid="nav-tree">
            <TreePine className="w-4 h-4" /> <span className="hidden sm:inline">Radici</span>
          </Link>
          <Link to="/doni" className={linkCls("/doni")} data-testid="nav-gifts">
            <Gift className="w-4 h-4" /> <span className="hidden sm:inline">Doni</span>
          </Link>
          <Link to="/album" className={linkCls("/album")} data-testid="nav-album">
            <Images className="w-4 h-4" /> <span className="hidden sm:inline">Album</span>
          </Link>
          <Link to="/registro-invii" className={linkCls("/registro-invii")} data-testid="nav-registro">
            <Mail className="w-4 h-4" /> <span className="hidden sm:inline">Registro</span>
          </Link>
          <Link to="/testamento" className={linkCls("/testamento")} data-testid="nav-testament">
            <ScrollText className="w-4 h-4" /> <span className="hidden sm:inline">Testamento</span>
          </Link>
          <Link to="/piani" className={linkCls("/piani")} data-testid="nav-plans">
            <Gem className="w-4 h-4" /> <span className="hidden sm:inline">Piani</span>
          </Link>
          {user?.role === "admin" && (
            <Link to="/verifica" className={linkCls("/verifica")} data-testid="nav-admin">
              <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">Verifiche</span>
            </Link>
          )}
          <Button
            onClick={() => navigate("/ricordi/nuovo")}
            className="rounded-full bg-primary hover:bg-[#c9694f] text-white ml-2"
            data-testid="nav-new-memory"
          >
            <PlusCircle className="w-4 h-4 mr-1" /> Nuovo
          </Button>
        </nav>
      </div>

      <Dialog open={coverOpen} onOpenChange={setCoverOpen}>
        <DialogContent className="bg-[#F9F6F0] rounded-2xl max-w-lg" data-testid="cover-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Foto di copertina</DialogTitle></DialogHeader>
          {coverOpen && <CoverImageCard embedded />}
        </DialogContent>
      </Dialog>

      <Dialog open={musicOpen} onOpenChange={setMusicOpen}>
        <DialogContent className="bg-[#F9F6F0] rounded-2xl max-w-lg" data-testid="music-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Musica della capsula</DialogTitle></DialogHeader>
          {musicOpen && <BackgroundMusicCard embedded />}
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Navbar;
